import { useId } from "react";
import { BRAND_NAME } from "@/brand";
import {
  SITEWRAP_LOGO_ASPECT,
  SITEWRAP_LOGO_PATH,
  SITEWRAP_LOGO_VIEWBOX
} from "@/assets/sitewrap-logo-path";

type SitewrapLogoProps = {
  /** Icon width in px (height follows mark aspect ratio) */
  size?: number;
  /** Show “Sitewrap” wordmark beside the mark */
  showWordmark?: boolean;
  /** `gradient` (default) or `mono` fill via currentColor */
  variant?: "gradient" | "mono";
  className?: string;
  href?: string;
  title?: string;
};

export default function SitewrapLogo({
  size = 28,
  showWordmark = true,
  variant = "gradient",
  className = "",
  href,
  title
}: SitewrapLogoProps) {
  const gradId = useId().replace(/:/g, "");
  const fill = variant === "gradient" ? `url(#${gradId})` : "currentColor";
  const wordClass =
    variant === "gradient" ? "sitewrapLogo__word sitewrapLogo__word--gradient" : "sitewrapLogo__word";
  const Tag = href ? "a" : "span";
  const height = Math.round(size / SITEWRAP_LOGO_ASPECT);

  return (
    <Tag
      className={`sitewrapLogo ${className}`.trim()}
      aria-label={showWordmark ? undefined : BRAND_NAME}
      {...(href ? { href, title } : {})}
    >
      <svg
        className="sitewrapLogo__mark"
        width={size}
        height={height}
        viewBox={SITEWRAP_LOGO_VIEWBOX}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={showWordmark ? true : undefined}
        role={showWordmark ? "presentation" : "img"}
      >
        {variant === "gradient" ? (
          <defs>
            <linearGradient id={gradId} x1="44" y1="52" x2="692" y2="732" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#60a5fa" />
              <stop offset="0.55" stopColor="#818cf8" />
              <stop offset="1" stopColor="#34d399" />
            </linearGradient>
          </defs>
        ) : null}
        <path d={SITEWRAP_LOGO_PATH} fill={fill} fillRule="evenodd" />
      </svg>
      {showWordmark ? <span className={wordClass}>{BRAND_NAME}</span> : null}
    </Tag>
  );
}
